/**
 * `BUILTIN_SHADERS` (W2-01b skins-and-builtin-effects; contract render/types.ts `BuiltinShaderDef`;
 * formats-and-packaging.md P§5.2, rendering.md R§9, DESIGN.md D24/R13).
 *
 * The ten built-in extra.json shaders Phira knows by name (chromatic, circleBlur, fisheye, glitch, grayscale, noise,
 * pixel, radialBlur, shockwave, vignette), REIMPLEMENTED from their behavioural description: our own GLSL ES 1.00
 * sources with Phira's uniform names and defaults, run by the effect runner like a package shader (defaults first,
 * then the effect's vars, then time / screenTexture / screenSize / UVScale). They are badged "approximate" until
 * verified against Phira captures (human checklist).
 *
 * Besides the contracted list, this module exports what the editor shows about each effect (names, descriptions,
 * uniform labels and ranges, the badge).
 */

import type { BuiltinShader } from '../../../../core/model/types';
import type { BuiltinShaderDef } from '../../../types';
import { chromatic } from './chromatic';
import { circleBlur } from './circleBlur';
import { fisheye } from './fisheye';
import { glitch } from './glitch';
import { grayscale } from './grayscale';
import { noise } from './noise';
import { pixel } from './pixel';
import { radialBlur } from './radialBlur';
import { shockwave } from './shockwave';
import type { BuiltinEffect, BuiltinFidelity } from './shared';
import { vignette } from './vignette';

export type { BuiltinEffect, BuiltinFidelity, BuiltinUniform, ScreenInput, UniformType, UniformValue } from './shared';
export { APPROXIMATE_BADGE, RESERVED_UNIFORMS } from './shared';
export { SIGNED_OFF, signOffOf, type BuiltinSignOff } from './signOff';

/** Every built-in effect with its editor metadata, keyed by shader name. */
export const BUILTIN_EFFECTS: Readonly<Record<BuiltinShader, BuiltinEffect>> = {
  chromatic,
  circleBlur,
  fisheye,
  glitch,
  grayscale,
  noise,
  pixel,
  radialBlur,
  shockwave,
  vignette,
};

/** The built-in shaders the effect runner compiles (one per Phira built-in name). */
export const BUILTIN_SHADERS: readonly BuiltinShaderDef[] = Object.values(BUILTIN_EFFECTS);

/** The built-in shader of a name (undefined: not previewed). */
export function builtinShader(name: BuiltinShader): BuiltinShaderDef | undefined {
  return BUILTIN_SHADERS.find((s) => s.name === name);
}

/**
 * How faithful the preview of a built-in effect is: 'approximate' for every reimplemented one until a person signs it
 * off (then 'verified': no badge); 'notPreviewed' when there is no shader for the name.
 */
export function builtinShaderFidelity(name: BuiltinShader): BuiltinFidelity | 'notPreviewed' {
  return builtinShader(name) === undefined ? 'notPreviewed' : BUILTIN_EFFECTS[name].fidelity;
}
